import { MouseEvent, ReactNode, useEffect, useRef } from 'react';
import { Box, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { color, EL_COLORS } from '@/colors';
import { LottieAnimation } from '@/components/Common/LottieAnimation';
import { MusicNoteIcon } from '@/UI/Icons/MusicNoteIcon';
import { PlayFilledIcon } from '@/UI/Icons/PlayFilledIcon';
import { FlexCenHor, FlexCenHorVer, FlexColCenHorVer } from '@/UI/styles';
import ThreeDeeViewer from '../../Nodes/ThreeDeeViewer';
import { TextViewer } from './TextViewer';
import type { ThreeDProps } from './types';
import type { MediaAsset, RenderingAsset, TextAsset } from '@/types/api/assets';

interface GalleryFileViewerProps extends Partial<ThreeDProps> {
  container: 'node' | 'gallery' | 'design-app';
  file: MediaAsset | TextAsset | RenderingAsset;
  handleImageLoad?: () => void;
  index: number;
  isHovered?: boolean;
  maxSteps?: number;
  onContextMenu?: (event: React.MouseEvent, index: number) => void;
  onTextChange?: (index: number, newValue: string) => void;
  overlay?: React.ReactNode;
  // eslint-disable-next-line react19-ref/require-ref-object-null
  resetTransformRef?: React.RefObject<ReactZoomPanPinchRef>;
  selected: number;
  setScale?: (scale: number) => void;
  transformKey?: number;
  // eslint-disable-next-line react19-ref/require-ref-object-null
  videoRef?: React.RefObject<HTMLVideoElement>;
  isMultiFilesViewMode?: boolean;
  fixedItemSize?: number;
}

const Container = ({
  children,
  isSelected,
  onContextMenu,
  isMultiFilesViewMode,
  minHeight,
}: {
  children: ReactNode;
  isSelected: boolean;
  onContextMenu: (event: MouseEvent) => void;
  isMultiFilesViewMode: boolean;
  minHeight?: number;
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: minHeight ? `${minHeight}px` : undefined,
        display: isMultiFilesViewMode || isSelected ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onContextMenu={onContextMenu}
    >
      {children}
    </Box>
  );
};

const GALLERY_SIZE = { w: 600, h: 600 };
const NODE_SIZE = { w: 422, h: 422 };

export const GalleryFileViewer: React.FC<GalleryFileViewerProps> = ({
  cameraPosition,
  container,
  file,
  handleImageLoad,
  index,
  is3DLocked,
  isHovered,
  maxSteps,
  onContextMenu,
  onTextChange,
  overlay = null,
  resetTransformRef,
  selected,
  setCameraPosition,
  setScale,
  transformKey,
  videoRef,
  isMultiFilesViewMode = false,
  fixedItemSize,
}) => {
  const isGallery = container === 'gallery' || container === 'design-app';
  const internalVideoElementRef = useRef<HTMLVideoElement>(null);
  // this is bit hacky, ideally we would want to have the video managed only in here
  // but since its a legacy component, we'll keep it for to support the single file view mode
  const videoElementRef = isMultiFilesViewMode ? internalVideoElementRef : videoRef;
  const audioElementRef = useRef<HTMLAudioElement>(null);

  const handleMediaPlayPause = (element: HTMLMediaElement | null) => {
    if (element) {
      if (isHovered) {
        void element.play();
      } else {
        void element.pause();
      }
    }
  };

  useEffect(() => {
    if (audioElementRef.current && index !== selected) {
      audioElementRef.current.pause();
    }
  }, [index, selected]);

  // Handle video play on hover only in multi-files view mode
  useEffect(() => {
    if (!isMultiFilesViewMode || file.type !== 'video' || !videoElementRef) {
      return;
    }

    handleMediaPlayPause(videoElementRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered, isMultiFilesViewMode, file.type]);

  // Handle audio play on hover only in multi-files view mode
  useEffect(() => {
    if (!isMultiFilesViewMode || file.type !== 'audio') {
      return;
    }

    handleMediaPlayPause(audioElementRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered, isMultiFilesViewMode, file.type]);

  const onContextMenuClick = (event: React.MouseEvent) => {
    onContextMenu?.(event, index);
  };

  const onZoom = (ref: ReactZoomPanPinchRef) => {
    setScale?.(ref.state.scale);
  };

  const isSelected = index === selected;
  const containerSize = fixedItemSize ? { w: fixedItemSize, h: fixedItemSize } : isGallery ? GALLERY_SIZE : NODE_SIZE;

  if (file.type === 'text' && isGallery) {
    return (
      <Container isSelected={isSelected} onContextMenu={onContextMenuClick} isMultiFilesViewMode={isMultiFilesViewMode}>
        <FlexCenHorVer>
          <Box
            sx={{
              width: '480px',
              maxHeight: '500px',
              overflow: 'auto',
              bgcolor: color.Black84,
              borderRadius: 1,
              border: `1px solid ${EL_COLORS.BoxBorder}`,
              p: 4,
            }}
          >
            <Typography variant="body-std-rg">{'value' in file ? file.value : ''}</Typography>
          </Box>
        </FlexCenHorVer>
      </Container>
    );
  }

  return (
    <Container
      isSelected={isSelected}
      onContextMenu={onContextMenuClick}
      isMultiFilesViewMode={isMultiFilesViewMode}
      minHeight={fixedItemSize}
    >
      {file.type === 'image' && (
        <>
          {container === 'node' ? (
            <img
              src={file.viewUrl || file.url}
              draggable="false"
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                position: 'relative',
                aspectRatio: file.width && file.height ? `${file.width}/${file.height} auto` : undefined,
              }}
            />
          ) : (
            <TransformWrapper
              centerZoomedOut
              limitToBounds
              minScale={1}
              pinch={{
                step: 100,
              }}
              onZoom={onZoom}
              ref={resetTransformRef}
              key={transformKey}
            >
              <TransformComponent>
                <img
                  className="media-container"
                  onLoad={handleImageLoad}
                  src={file.url}
                  draggable="false"
                  style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', margin: 'auto' }}
                />
              </TransformComponent>
            </TransformWrapper>
          )}
        </>
      )}

      {file.type === 'video' ? (
        <>
          <video
            key={`video-${index}`}
            crossOrigin="anonymous"
            draggable="false"
            src={file.url}
            controls={isMultiFilesViewMode ? false : true}
            loop
            muted={isMultiFilesViewMode}
            ref={videoElementRef}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              position: 'relative',
              aspectRatio: file.width && file.height ? `${file.width}/${file.height} auto` : undefined,
            }}
          />
          {isMultiFilesViewMode && !isHovered && (
            <Box
              sx={{
                position: 'absolute',
              }}
            >
              <PlayFilledIcon />
            </Box>
          )}
        </>
      ) : null}

      {(isMultiFilesViewMode || isSelected) && file.type === '3D' && (
        <FlexCenHor
          className={container === 'node' ? 'nowheel nodrag nopan' : ''}
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            alignItems: 'center',
          }}
        >
          <ThreeDeeViewer
            key={file.url}
            objUrl={file.url}
            containerSize={containerSize}
            lockOrbit={is3DLocked || false}
            cameraPosition={cameraPosition || { x: -3, y: 4, z: 5 }}
            setCameraPosition={setCameraPosition}
            allowInteraction={!isMultiFilesViewMode}
            displayBackground={isMultiFilesViewMode}
          />
        </FlexCenHor>
      )}

      {file.type === 'audio' && (
        <>
          <FlexCenHorVer sx={{ height: '100%', width: '100%' }}>
            <audio
              crossOrigin="anonymous"
              draggable="false"
              src={file.url}
              controls={isMultiFilesViewMode ? false : true}
              loop={true}
              ref={audioElementRef}
            />
          </FlexCenHorVer>
          {isMultiFilesViewMode && (
            <Box
              sx={{
                position: 'absolute',
              }}
            >
              {isHovered ? (
                <LottieAnimation width={42} height={42} loop={true} src="/audio-wave.lottie" />
              ) : (
                <MusicNoteIcon />
              )}
            </Box>
          )}
        </>
      )}

      {file.type === 'text' && !isGallery && (
        <TextViewer
          file={file}
          index={index}
          onTextChange={onTextChange}
          hasMultipleFiles={maxSteps !== undefined && maxSteps > 1}
          isMultiFilesViewMode={isMultiFilesViewMode}
          fixedItemSize={fixedItemSize}
        />
      )}

      {file.type === 'rendering' && (
        <FlexCenHorVer
          sx={{
            height: '100%',
            width: '100%',
            borderRadius: 2,
            border: `1px solid ${color.White64_T}`,
            backgroundColor: color.Black100,
          }}
        >
          {file.progress !== undefined ? (
            <FlexColCenHorVer sx={{ width: '80%', gap: 0.5 }}>
              <Typography
                variant="body-sm-rg"
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  fontSize: '2.75rem',
                  lineHeight: '2.8rem',
                  fontWeight: '500',
                }}
              >
                {file.progress}%
              </Typography>
              <LinearProgress sx={{ width: '100%' }} color="weavy_cta" variant="determinate" value={file.progress} />
            </FlexColCenHorVer>
          ) : (
            <CircularProgress color="weavy_cta" />
          )}
        </FlexCenHorVer>
      )}
      {overlay}
      {container === 'node' && !isMultiFilesViewMode ? (
        <Box
          data-testid="gradient-overlay-container"
          sx={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            pointerEvents: 'none',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.1s ease-in-out',
          }}
        >
          <FlexCenHor
            data-testid="bottom-gradient-overlay"
            sx={{
              position: 'absolute',
              bottom: 0,
              width: '100%',
              height: '80px',
              background: 'linear-gradient(to top,#343337D3, #6B6B7800)',
              pointerEvents: 'none',
              filter: 'blur(30px)',
              transform: 'scale(1.8)  translateY(30px)',
            }}
          />
          <FlexCenHor
            data-testid="top-gradient-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              width: '100%',
              height: '80px',
              background: 'linear-gradient(to bottom,#343337D3, #6B6B7800)',
              pointerEvents: 'none',
              filter: 'blur(30px)',
              transform: 'scale(1.8)  translateY(-30px)',
            }}
          />
        </Box>
      ) : null}
    </Container>
  );
};
